import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './pages/Home';

// Rendimiento: solo el inicio va en el paquete principal. Cada página interior
// se descarga cuando se visita (antes todo el sitio, con las ~3.600 líneas del
// blog, MUI y Swiper, bajaba en un solo archivo de 257 KB gzip antes de pintar
// el inicio).
const Services = lazy(() => import('./pages/Services'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Licenses = lazy(() => import('./pages/Licenses'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Products = lazy(() => import('./components/Products/Products'));
// El blog es lo único que sigue con MUI: se carga junto con su tema.
const Blog = lazy(() => import('./pages/BlogConTema').then((m) => ({ default: m.Blog })));
const BlogPost = lazy(() => import('./pages/BlogConTema').then((m) => ({ default: m.BlogPost })));

// Las tarjetas de licencias de la Home apuntaban a /licencias/office365,
// /licencias/windows, /licencias/sql-server y /licencias/azure, rutas que nunca
// existieron. El contenido de los cuatro productos ya vive en /licencias, así
// que en vez de crear cuatro páginas casi duplicadas se redirige al ancla
// correspondiente. Esto cubre además cualquier URL que Google ya haya rastreado.
function RedirigirLicencia() {
  const { producto } = useParams();
  return <Navigate to={`/licencias#${producto}`} replace />;
}

// En una SPA el navegador no sabe que cambió de página: sin esto, al navegar
// se queda el scroll de la página anterior y el lector de pantalla no anuncia
// nada. Al cambiar de ruta se sube al inicio y el foco pasa al <main> (salvo en
// la primera carga y cuando la URL trae un ancla, que maneja la propia página).
function AlCambiarDeRuta({ principal }) {
  const { pathname, hash } = useLocation();
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) {
      primera.current = false;
      return;
    }
    if (hash) return;
    window.scrollTo(0, 0);
    principal.current?.focus({ preventScroll: true });
  }, [pathname, hash, principal]);
  return null;
}

// Reserva el alto de la página mientras llega el código de una ruta interior,
// para que el pie no salte hacia arriba y vuelva a bajar.
function Cargando() {
  return <div className="cargando-ruta" aria-hidden="true" />;
}

function App() {
  const principal = useRef(null);
  return (
    <Router>
      <div className="App">
        <a className="saltar" href="#contenido">
          Saltar al contenido
        </a>
        <Navbar />
        <AlCambiarDeRuta principal={principal} />
        <main id="contenido" ref={principal} tabIndex={-1}>
          <Suspense fallback={<Cargando />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/servicios" element={<Services />} />
              <Route path="/servicios/:slug" element={<ServicePage />} />
              <Route path="/nosotros" element={<About />} />
              <Route path="/contacto" element={<Contact />} />
              <Route path="/licencias" element={<Licenses />} />
              <Route path="/licencias/:producto" element={<RedirigirLicencia />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/politica-privacidad" element={<Privacy />} />
              <Route path="/terminos-condiciones" element={<Terms />} />
              <Route path="/productos" element={<Products />} />
              {/* Comodín: cualquier otra ruta cae en la 404 del sitio en vez de
                  dejar el <main> vacío entre el menú y el pie. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
